import { ajaxRequest, app, ISystem, MainTemplate, routing, store, ThemeModule, useForceUpdate, useMapState } from "@comact/crc";
import { LoadingSpinner } from "@comact/crc/modules/kit";
import React from "react";
import { FeatureFlagsModule } from "./featureFlags";
import { getB2cPolicies, getMsalInstance, initMsalConfig, recoverSignIn } from "./msal/msalAuthoConfig";
import MsalConditionalProvider from "./msal/MsalConditionalProvider";
import { endRecovery } from "./msal/signInRecovery";
import WaitForMsalLogin from "./msal/WaitForMsalLogin";
import SwitchUiHeaderItem from "./uiSwitch/SwitchUiHeaderItem";
import { NodesModule } from "./node";
import { Levels, Permissions, UserModule } from "./users";

/**
 * Return the system data needed for the app initialization.
 */
export const getSystemData = async ({ serverLess }: { serverLess: boolean; standalone: boolean; }): Promise<ISystem> => {
    if (!serverLess) await initMsalConfig(); // needed to be done before any rendering

    let isLoggedIn = serverLess || UserModule.selectors.isLoggedIn(store.getState());

    while (!isLoggedIn) {
        await new Promise((r) => setTimeout(r, 100));
        isLoggedIn = UserModule.selectors.isLoggedIn(store.getState());
    }

    if (isLoggedIn) {
        await Promise.all([
            UserModule.getRequests(store.dispatch).getMyAccountOrganizations().promise,
            UserModule.getRequests(store.dispatch).getMyAccountUsers().promise,
            UserModule.getRequests(store.dispatch).getOrganizationGroups().promise,
        ]);
    }

    const user = UserModule.selectors.getCurrentUser(store.getState());

    if (!user?.theme) console.info("%cThe user theme has not been provided by the server, it might be not implemented yet.", "background-color: #ff2600; color: #fff; padding: 1px 3px;");
    else store.dispatch(ThemeModule.actions.set(user.theme)); // set the theme

    // Return language and system for initialization
    return {
        activeLocale: user?.locale || "en",
        availableLocales: ["en", "fr"],
        defaultLocale: "en",
        requiredLocales: ["en"],
        unitSystem: "imperial",
        realUnitSystem: "imperial",
        nominalUnitSystem: "imperial",
        initialConfigurationRequired: false,
        isMaster: true,
        hasTwin: true,
        useNodes: true,
        version: "OPER8",
        systemTheme: null, // not implemented yet
    };
};

export const RootContainer = React.memo(({ children }: { children: React.ReactNode; }) => {
    if (app.serverLess) return children;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [isReady, setIsReady] = React.useState(false);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const forceUpdate = useForceUpdate();

    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/rules-of-hooks
    React.useEffect(() => {
        const msal = getMsalInstance();
        if (msal) {
            setIsReady(true);
        } else {
            setTimeout(forceUpdate, 50);
        }
    });

    if (!isReady) return <LoadingSpinner.AbsoluteScreen />;

    return <MsalConditionalProvider children={children} />;
});

export const PageContainer = React.memo(({ children }: { children: React.ReactNode; }) => {
    const hideEmail = useMapState((state) => {
        const locationNodeId = NodesModule.selectors.getCurrentLocationNodeId(state);
        return (
            !locationNodeId ||
            (FeatureFlagsModule.selectors.hasRequiredFeatureFlags(state, ["Salesforce"]) &&
            UserModule.selectors.hasPermission(state, [{ permission: Permissions.ServiceRequest, level: Levels.Edit }], locationNodeId)
            )
        );
    });
    if (app.serverLess) return children;
    return (
        <WaitForMsalLogin>
            <SwitchUiHeaderItem />
            <MainTemplate children={children} userCanOpenVersionModal={false} showHamburgerMenu={true} hideEmail={hideEmail} />
        </WaitForMsalLogin>
    );
});

export const getAjaxRequestHeaders = async () => {
    const requestHeaders: Record<string, string> = {};
    const msalInstance = getMsalInstance();
    try {
        const response = await msalInstance.acquireTokenSilent({ scopes: [getB2cPolicies().readScope], account: msalInstance.getActiveAccount(), redirectUri: routing.projectPath });
        endRecovery(sessionStorage);
        requestHeaders.Authorization = `${response.tokenType} ${response.accessToken}`;
    } catch (error) {
        // A dead grant (expired refresh token) never renews by itself: start a clean sign-in. The request still fails, the page is leaving.
        await recoverSignIn(error, routing.projectPath);
        throw error;
    }
    return requestHeaders;
};

export const ajaxRequestDefaults = { getAjaxRequestHeaders, timeout: [10000, 15000, 30000], backoffFactor: 2 };

export const getVersionsInfos = async () => {
    try {
        const serverVersionInfos = await ajaxRequest({ url: "/api/v1/version" }).promise;
        return [serverVersionInfos];
    } catch {
        return [];
    }
};